import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Mail, Lock, ArrowRight, Eye, EyeOff, Shield } from "lucide-react";
import { toast } from "sonner";

function getDeviceFingerprint() {
  const nav = navigator;
  const screen = window.screen;
  const browser = nav.userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)[\/\s](\d+)/)?.[0] || nav.userAgent.slice(0, 40);
  const os = nav.platform || nav.userAgentData?.platform || "Unknown";
  const screenSize = `${screen.width}x${screen.height}`;
  const raw = `${browser}|${os}|${screenSize}|${nav.language}|${nav.hardwareConcurrency || ""}|${screen.colorDepth}`;
  // Simple hash
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return {
    fingerprint: Math.abs(hash).toString(36) + raw.length.toString(36),
    browser,
    os,
    screen: screenSize,
  };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, initiateGoogleLogin, initiateMicrosoftLogin } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [deviceVerificationRequired, setDeviceVerificationRequired] = useState(false);

  const from = location.state?.from?.pathname || "/dashboard";

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDeviceVerificationRequired(false);

    try {
      const deviceInfo = getDeviceFingerprint();
      const userData = await login(formData.email, formData.password, deviceInfo);
      toast.success("Welcome back!");
      const isAdminUser = userData.user_type === "admin" || userData.user_type === "super_admin" || userData.user_type === "sub_admin" || userData.user_type === "school_admin";
      if (isAdminUser && from === "/dashboard") {
        navigate("/admin", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.data?.detail === "device_verification_required") {
        setDeviceVerificationRequired(true);
      } else {
        toast.error(error.response?.data?.detail || "Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    initiateGoogleLogin();
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    try {
      await initiateMicrosoftLogin();
    } catch (error) {
      console.error("Microsoft login error:", error);
      toast.error("Failed to initiate Microsoft login");
      setMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 gradient-mesh">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-elevated rounded-3xl overflow-hidden">
          <CardContent className="p-8 md:p-10">
            {/* Logo */}
            <Link to="/" className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-glow">
                <img src="/logo.png" alt="Great Messiah Publication" className="w-full h-full object-contain" />
              </div>
              <span className="font-heading text-2xl font-bold">Great Messiah</span>
            </Link>
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold mb-2">Welcome Back</h1>
              <p className="text-muted-foreground">Sign in to access your account</p>
            </div>

            {/* Device Verification Required */}
            {deviceVerificationRequired && (
              <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="device-verification-notice">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">New Device Detected</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Your account has been signed in on multiple devices. For your security, we've sent a verification email to your registered email address. Please check your inbox and click the verification link to continue on this device.
                    </p>
                    <p className="text-xs text-amber-600 mt-2 font-medium">
                      Didn't receive it? Try logging in again to resend the email.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* OAuth Buttons */}
            <div className="space-y-3 mb-6">
              {/* Google OAuth Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-xl border-gray-300 hover:bg-gray-50 text-gray-700 font-medium"
                onClick={handleGoogleLogin}
                disabled={googleLoading || microsoftLoading}
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </Button>

              {/* Microsoft OAuth Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-xl border-gray-300 hover:bg-gray-50 text-gray-700 font-medium"
                onClick={handleMicrosoftLogin}
                disabled={microsoftLoading || googleLoading}
                data-testid="microsoft-login-btn"
              >
                {microsoftLoading ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                )}
                Continue with Microsoft
              </Button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-gray-500">Or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="pl-12 h-12 rounded-xl border-border/50 focus:border-primary"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="pl-12 pr-12 h-12 rounded-xl border-border/50 focus:border-primary"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="login-toggle-password"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-base font-medium shadow-glow btn-press"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Sign up link */}
            <div className="mt-8 text-center">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-primary font-semibold hover:underline">
                Sign up
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Trust indicators */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Trusted by 1000+ schools and 50,000+ students in Ghana
        </p>
      </div>
    </div>
  );
}
